import React, { useState } from "react";
import { Button, Classes, IconName, Text } from "@blueprintjs/core";
import { TASKS_QUERY_FILTER } from "ui-io/queries/tasks/task-query";
import { delay } from "ui-lib";
import { TaskAssignmentActionTypeT, TaskT } from "ui-io/task-io/task-t";
import { FsmGroupT } from "ui-io/queries/tasks/task-user-list-query";
import { Box, ThemedDialog } from "ui-components";
import { taskEventMutation } from "ui-io/queries/tasks/task-event-mutation";
import {
  taskAssignMutation,
  TaskAssignMutationResultT,
} from "ui-io/queries/tasks/task-assign-from-backlog-mutation";
import { useMutation, useQueryClient } from "react-query";
import { AssignmentTypeT } from "ui-io/task-io";

import toasts from "../../../utils/toasts";
import UserInputGroup from "./user-input-group";
import useSession from "ui-io/use-session";
import { TASKS_SEARCH_QUERY } from "ui-io/queries/tasks/tasks-query";

type ModifyAssignmentDialogProps = {
  task: TaskT;
  actionType: TaskAssignmentActionTypeT;
  setTaskAction: (taskAction: TaskAssignmentActionTypeT | null) => void;
  assignmentType?: AssignmentTypeT;
  allUserList?: string[];
};
type UserDetailsT = {
  user: string;
  fsmGroup?: FsmGroupT;
};
type actionMetaValueT = {
  icon: IconName;
  title: string;
  actionDescription?: (brandName?: string) => string;
};

const actionMetaData: Record<TaskAssignmentActionTypeT, actionMetaValueT> = {
  UNASSIGN: {
    icon: "undo",
    title: "Unassign",
    actionDescription: (brandName) =>
      `This action will remove the task ${brandName} from your inbox. Please confirm to proceed.`,
  },
  REASSIGN: {
    icon: "following",
    title: "Reassign",
  },
  SELF_ASSIGN: {
    icon: "following",
    title: "Self Assign",
    actionDescription: (brandName) =>
      `This action will add the task ${brandName} to your inbox. Please confirm to proceed.`,
  },
};

const ModifyAssignmentDialog = ({
  task,
  actionType,
  setTaskAction,
  assignmentType,
  allUserList,
}: ModifyAssignmentDialogProps) => {
  const { session } = useSession();
  const username = session?.username;
  const queryClient = useQueryClient();
  const [userDetail, setUserDetail] = useState<UserDetailsT | null>(null);

  const onSuccess = async () => {
    const successMessage = task
      ? `Successfully ${actionType.toLowerCase()}ed ${assignmentType?.toLowerCase()} task for ${
          task.title
        }!!`
      : "";
    toasts.success(successMessage);
    setTaskAction(null);
    await delay(500);
    queryClient.refetchQueries([TASKS_QUERY_FILTER]);
    queryClient.refetchQueries([TASKS_SEARCH_QUERY]);
  };

  const onSelfAssignSuccess = (res: TaskAssignMutationResultT) => {
    if (res.length > 0 && res[0].task_id === task.id) {
      onSuccess();
    } else {
      onError(new Error("Could not assign the requested task."));
    }
  };

  const onError = (error: Error) => {
    console.error(error.message);
    toasts.error(`Error: Could not assign the requested task.`);
    setTaskAction(null);
  };
  const eventMutation = useMutation<any, Error, any>(taskEventMutation, {
    onError,
    onSuccess,
  });
  const assignmentMutation = useMutation(taskAssignMutation, {
    onError,
    onSuccess: onSelfAssignSuccess,
  });
  type ModifyAssignmentPropsT = {
    taskId: string;
    actionType: TaskAssignmentActionTypeT;
    configUuid?: string;
    taskType?: string;
  };
  const modifyAssignment = ({
    taskId,
    actionType,
    configUuid,
    taskType,
  }: ModifyAssignmentPropsT) => {
    const filterOnConfigUuid = "item.web_scrape_config.uuid:" + configUuid;
    const filterOnType = taskType ? "item.type:" + taskType : undefined;

    let todoFilters = [filterOnConfigUuid];
    if (filterOnType) todoFilters.push(filterOnType);

    const updatedBy = username;
    switch (actionType) {
      case "REASSIGN":
        if (userDetail?.user) {
          eventMutation.mutate({
            taskId,
            user: userDetail.user,
            eventType: "REASSIGN",
            updatedBy,
          });
        }

        break;
      case "UNASSIGN":
        eventMutation.mutate({
          taskId,
          eventType: task.isInProgress ? "UNASSIGN_EDITOR" : "UNASSIGN",
          updatedBy,
        });
        break;
      case "SELF_ASSIGN":
        assignmentMutation.mutate({
          payload: {
            assignments: ["CONFIG"],
            todo_filters: todoFilters,
          },
        });

        break;
    }
  };

  const submitAssignmentAction = (task: TaskT) => {
    if (eventMutation.isLoading || assignmentMutation.isLoading) {
      return;
    }
    const { id, configUuid, type } = task;
    modifyAssignment({
      taskId: id,
      actionType,
      configUuid,
      taskType: type,
    });
  };
  return (
    <ThemedDialog
      icon={actionMetaData[actionType].icon}
      isOpen={actionType !== null}
      title={actionMetaData[actionType].title}
      onClose={() => {
        setTaskAction(null);
      }}
      canEscapeKeyClose
    >
      <div className={Classes.DIALOG_BODY}>
        <Box>
          <Text>
            {actionMetaData[actionType].actionDescription?.(task.title) || ""}
          </Text>
        </Box>
        {actionType === "REASSIGN" && !!task.id ? (
          <Box mt={2}>
            <UserInputGroup
              {...{
                setUserDetail,
                userDetail,
                taskFSMId: task.id,
                assignmentType,
                allUserList,
              }}
            />
          </Box>
        ) : null}
      </div>
      <div className={Classes.DIALOG_FOOTER}>
        <div className={Classes.DIALOG_FOOTER_ACTIONS}>
          <Button
            onClick={() => {
              setTaskAction(null);
            }}
            text="Cancel"
          />
          <Button
            text="Confirm"
            loading={eventMutation.isLoading || assignmentMutation.isLoading}
            intent="primary"
            onClick={() => {
              submitAssignmentAction(task);
            }}
            disabled={actionType === "REASSIGN" && !userDetail}
          />
        </div>
      </div>
    </ThemedDialog>
  );
};

export default ModifyAssignmentDialog;
